/* eslint-disable */
/**
 * DividendPage — Track dividend income from your holdings
 *
 * Props:
 *   supabase   : supabase client
 *   user       : current user object
 *   isMobile   : boolean
 *   demoItems  : optional demo data array
 */
import { useState, useEffect, useRef } from "react";
import { C, fmt$ } from "../shared/globals.js";

const FM = "Montserrat,sans-serif";

const fmtPrice = v => {
  if (!v && v !== 0) return "—";
  if (v < 0.001) return "$" + v.toFixed(6);
  if (v < 1)     return "$" + v.toFixed(4);
  return fmt$(v);
};

const fmtDate = iso => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" });
};

const fmtDateShort = iso => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month:"short", day:"numeric" });
};

// Days until a date
const daysUntil = iso => {
  if (!iso) return null;
  const diff = new Date(iso) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// ── Add/Edit Modal ────────────────────────────────────────────────────────────
function DividendModal({ item, onSave, onClose }) {
  const [ticker,      setTicker]      = useState(item?.ticker || "");
  const [name,        setName]        = useState(item?.name || "");
  const [shares,      setShares]      = useState(item?.shares || "");
  const [annualDiv,   setAnnualDiv]   = useState(item?.annual_dividend || "");
  const [exDate,      setExDate]      = useState(item?.ex_date || "");
  const [payDate,     setPayDate]     = useState(item?.pay_date || "");
  const [frequency,   setFrequency]   = useState(item?.frequency || "quarterly");
  const [costBasis,   setCostBasis]   = useState(item?.cost_basis || "");
  const [note,        setNote]        = useState(item?.note || "");
  const [saving,      setSaving]      = useState(false);

  const annualIncome  = annualDiv && shares ? (parseFloat(annualDiv) * parseFloat(shares)).toFixed(2) : null;
  const yieldOnCost   = annualDiv && costBasis ? ((parseFloat(annualDiv) / parseFloat(costBasis)) * 100).toFixed(2) : null;

  const handleSubmit = async () => {
    if (!ticker.trim()) return;
    setSaving(true);
    await onSave({
      ticker:           ticker.toUpperCase().trim(),
      name:             name.trim() || ticker.toUpperCase().trim(),
      shares:           parseFloat(shares) || 0,
      annual_dividend:  parseFloat(annualDiv) || 0,
      ex_date:          exDate || null,
      pay_date:         payDate || null,
      frequency,
      cost_basis:       parseFloat(costBasis) || 0,
      note:             note.trim() || null,
    });
    setSaving(false);
  };

  const inp = {
    width:"100%", background:"var(--bg3)", border:"1px solid var(--border)",
    borderRadius:8, padding:"9px 12px", fontFamily:FM, fontSize:12,
    fontWeight:600, color:"var(--text)", outline:"none", boxSizing:"border-box",
    colorScheme:"dark",
  };

  const lbl = { fontFamily:FM, fontSize:9, fontWeight:700, color:"var(--text4)", letterSpacing:"0.12em", marginBottom:5, display:"block" };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:9000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:14, padding:24, width:"100%", maxWidth:480, maxHeight:"90vh", overflowY:"auto" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <div style={{ fontFamily:FM, fontSize:13, fontWeight:900, color:"var(--text)", letterSpacing:"0.05em" }}>
            {item ? "EDIT HOLDING" : "ADD DIVIDEND STOCK"}
          </div>
          <button onClick={onClose} style={{ background:"transparent", border:"none", color:"var(--text4)", cursor:"pointer", fontSize:18, lineHeight:1 }}>✕</button>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <div style={{ gridColumn:"1/-1" }}>
            <label style={lbl}>TICKER</label>
            <input value={ticker} onChange={e => setTicker(e.target.value.toUpperCase())} style={inp} placeholder="e.g. AAPL" maxLength={8}/>
          </div>
          <div style={{ gridColumn:"1/-1" }}>
            <label style={lbl}>COMPANY NAME</label>
            <input value={name} onChange={e => setName(e.target.value)} style={inp} placeholder="e.g. Apple Inc."/>
          </div>
          <div>
            <label style={lbl}>SHARES OWNED</label>
            <input type="number" value={shares} onChange={e => setShares(e.target.value)} style={inp} placeholder="0" min="0" step="0.01"/>
          </div>
          <div>
            <label style={lbl}>COST BASIS / SHARE ($)</label>
            <input type="number" value={costBasis} onChange={e => setCostBasis(e.target.value)} style={inp} placeholder="0.00" min="0" step="0.01"/>
          </div>
          <div>
            <label style={lbl}>ANNUAL DIVIDEND / SHARE ($)</label>
            <input type="number" value={annualDiv} onChange={e => setAnnualDiv(e.target.value)} style={inp} placeholder="0.00" min="0" step="0.001"/>
          </div>
          <div>
            <label style={lbl}>FREQUENCY</label>
            <select value={frequency} onChange={e => setFrequency(e.target.value)} style={{ ...inp, cursor:"pointer" }}>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="semi-annual">Semi-Annual</option>
              <option value="annual">Annual</option>
            </select>
          </div>
          <div>
            <label style={lbl}>NEXT EX-DATE</label>
            <input type="date" value={exDate} onChange={e => setExDate(e.target.value)} style={inp}/>
          </div>
          <div>
            <label style={lbl}>NEXT PAY DATE</label>
            <input type="date" value={payDate} onChange={e => setPayDate(e.target.value)} style={inp}/>
          </div>
          <div style={{ gridColumn:"1/-1" }}>
            <label style={lbl}>NOTE</label>
            <input value={note} onChange={e => setNote(e.target.value)} style={inp} placeholder="Optional note..." maxLength={200}/>
          </div>
        </div>

        {/* Live preview */}
        {(annualIncome || yieldOnCost) && (
          <div style={{ marginTop:16, padding:"10px 14px", background:"var(--bg3)", borderRadius:8, border:"1px solid var(--border)", display:"flex", gap:20 }}>
            {annualIncome && (
              <div>
                <div style={{ fontFamily:FM, fontSize:8, color:"var(--text4)", letterSpacing:"0.1em", marginBottom:3 }}>ANNUAL INCOME</div>
                <div style={{ fontFamily:FM, fontSize:14, fontWeight:800, color:C.green }}>${parseFloat(annualIncome).toLocaleString()}</div>
              </div>
            )}
            {yieldOnCost && (
              <div>
                <div style={{ fontFamily:FM, fontSize:8, color:"var(--text4)", letterSpacing:"0.1em", marginBottom:3 }}>YIELD ON COST</div>
                <div style={{ fontFamily:FM, fontSize:14, fontWeight:800, color:C.orange }}>{yieldOnCost}%</div>
              </div>
            )}
          </div>
        )}

        <div style={{ display:"flex", gap:8, marginTop:20 }}>
          <button onClick={onClose} style={{ flex:1, background:"transparent", border:"1px solid var(--border)", borderRadius:8, padding:"10px", cursor:"pointer", fontFamily:FM, fontSize:10, fontWeight:700, color:"var(--text4)" }}>
            CANCEL
          </button>
          <button onClick={handleSubmit} disabled={saving || !ticker.trim()} style={{
            flex:2, background:C.orange, border:"none", borderRadius:8, padding:"10px",
            cursor: saving || !ticker.trim() ? "not-allowed" : "pointer",
            fontFamily:FM, fontSize:10, fontWeight:800, color:"#000",
            opacity: saving || !ticker.trim() ? 0.6 : 1,
          }}>
            {saving ? "SAVING…" : item ? "SAVE CHANGES" : "ADD HOLDING"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Log Payment Modal ─────────────────────────────────────────────────────────
function LogPaymentModal({ holding, onSave, onClose }) {
  const [amount,    setAmount]    = useState(
    holding?.annual_dividend && holding?.shares
      ? ((holding.annual_dividend * holding.shares) / (holding.frequency === "monthly" ? 12 : holding.frequency === "quarterly" ? 4 : holding.frequency === "semi-annual" ? 2 : 1)).toFixed(2)
      : ""
  );
  const [payDate,   setPayDate]   = useState(holding?.pay_date || new Date().toISOString().split("T")[0]);
  const [saving,    setSaving]    = useState(false);

  const inp = { width:"100%", background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:8, padding:"9px 12px", fontFamily:FM, fontSize:12, fontWeight:600, color:"var(--text)", outline:"none", boxSizing:"border-box", colorScheme:"dark" };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:9001, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:14, padding:24, width:"100%", maxWidth:360 }}>
        <div style={{ fontFamily:FM, fontSize:13, fontWeight:900, color:"var(--text)", marginBottom:20 }}>
          LOG PAYMENT — {holding?.ticker}
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          <div>
            <div style={{ fontFamily:FM, fontSize:9, fontWeight:700, color:"var(--text4)", letterSpacing:"0.12em", marginBottom:5 }}>AMOUNT RECEIVED ($)</div>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)} style={inp} placeholder="0.00" min="0" step="0.01"/>
          </div>
          <div>
            <div style={{ fontFamily:FM, fontSize:9, fontWeight:700, color:"var(--text4)", letterSpacing:"0.12em", marginBottom:5 }}>PAYMENT DATE</div>
            <input type="date" value={payDate} onChange={e => setPayDate(e.target.value)} style={inp}/>
          </div>
        </div>
        <div style={{ display:"flex", gap:8, marginTop:20 }}>
          <button onClick={onClose} style={{ flex:1, background:"transparent", border:"1px solid var(--border)", borderRadius:8, padding:"10px", cursor:"pointer", fontFamily:FM, fontSize:10, fontWeight:700, color:"var(--text4)" }}>CANCEL</button>
          <button onClick={async () => { setSaving(true); await onSave({ holding_id: holding.id, ticker: holding.ticker, amount: parseFloat(amount) || 0, pay_date: payDate }); setSaving(false); }} style={{ flex:2, background:C.green, border:"none", borderRadius:8, padding:"10px", cursor:"pointer", fontFamily:FM, fontSize:10, fontWeight:800, color:"#000", opacity: saving ? 0.6 : 1 }}>
            {saving ? "SAVING…" : "LOG PAYMENT"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Mini bar chart ────────────────────────────────────────────────────────────
function MonthlyBarChart({ payments }) {
  const byMonth = Array(12).fill(0);
  payments.forEach(p => {
    const m = new Date(p.pay_date).getMonth();
    byMonth[m] += p.amount;
  });
  const maxVal = Math.max(...byMonth, 1);
  const now = new Date().getMonth();

  return (
    <div style={{ display:"flex", alignItems:"flex-end", gap:4, height:60 }}>
      {byMonth.map((v, i) => (
        <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
          <div style={{
            width:"100%", borderRadius:"3px 3px 0 0",
            height: Math.max(2, (v / maxVal) * 52),
            background: i === now ? C.orange : v > 0 ? C.green : "var(--border)",
            transition:"height 0.3s ease",
            position:"relative",
          }}/>
          <div style={{ fontFamily:FM, fontSize:6, color: i === now ? C.orange : "var(--text5)", fontWeight:700 }}>{MONTHS[i].slice(0,1)}</div>
        </div>
      ))}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function DividendPage({ supabase, user, isMobile = false, demoItems = null }) {
  const [holdings,     setHoldings]     = useState([]);
  const [payments,     setPayments]     = useState([]);
  const [prices,       setPrices]       = useState({});
  const [loading,      setLoading]      = useState(true);
  const [pricesLoading,setPricesLoading]= useState(false);
  const [showModal,    setShowModal]    = useState(false);
  const [editItem,     setEditItem]     = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [logItem,      setLogItem]      = useState(null);
  const [deleteConfirm,setDeleteConfirm]= useState(null);
  const [activeTab,    setActiveTab]    = useState("holdings"); // holdings | history | calendar

  // ── Load from Supabase ──────────────────────────────────────────────────────
  useEffect(() => {
    if (demoItems) {
      setHoldings(demoItems.holdings || []);
      setPayments(demoItems.payments || []);
      setLoading(false);
      return;
    }
    if (!user) return;
    (async () => {
      setLoading(true);
      const [h, p] = await Promise.all([
        supabase.from("dividend_holdings").select("*").eq("user_id", user.id).order("created_at", { ascending:false }),
        supabase.from("dividend_payments").select("*").eq("user_id", user.id).order("pay_date", { ascending:false }),
      ]);
      if (!h.error) setHoldings(h.data || []);
      if (!p.error) setPayments(p.data || []);
      setLoading(false);
    })();
  }, [user, demoItems]);

  // ── Fetch live prices ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!holdings.length) return;
    (async () => {
      setPricesLoading(true);
      const results = {};
      await Promise.all(holdings.map(async h => {
        try {
          const res = await fetch(`/api/mstr-price?ticker=${encodeURIComponent(h.ticker)}&t=${Date.now()}`);
          if (res.ok) {
            const d = await res.json();
            results[h.id] = { price: d.price || 0 };
          }
        } catch {}
      }));
      setPrices(results);
      setPricesLoading(false);
    })();
  }, [holdings.length]);

  // ── CRUD ────────────────────────────────────────────────────────────────────
  const handleSave = async (data) => {
    if (demoItems) return;
    if (editItem) {
      const { error } = await supabase.from("dividend_holdings").update(data).eq("id", editItem.id);
      if (!error) setHoldings(prev => prev.map(h => h.id === editItem.id ? { ...h, ...data } : h));
    } else {
      const { data: inserted, error } = await supabase.from("dividend_holdings").insert({ ...data, user_id: user.id }).select().single();
      if (!error) setHoldings(prev => [inserted, ...prev]);
    }
    setShowModal(false);
    setEditItem(null);
  };

  const handleDelete = async (id) => {
    if (demoItems) return;
    await supabase.from("dividend_holdings").delete().eq("id", id);
    setHoldings(prev => prev.filter(h => h.id !== id));
    setDeleteConfirm(null);
  };

  const handleLogPayment = async (data) => {
    if (demoItems) return;
    const { data: inserted, error } = await supabase.from("dividend_payments").insert({ ...data, user_id: user.id }).select().single();
    if (!error) setPayments(prev => [inserted, ...prev]);
    setShowLogModal(false);
    setLogItem(null);
  };

  // ── Computed summary ────────────────────────────────────────────────────────
  const now = new Date();
  const ytdPayments  = payments.filter(p => new Date(p.pay_date).getFullYear() === now.getFullYear());
  const ytdTotal     = ytdPayments.reduce((s, p) => s + (p.amount || 0), 0);
  const annualIncome = holdings.reduce((s, h) => s + (h.annual_dividend || 0) * (h.shares || 0), 0);
  const monthlyIncome= annualIncome / 12;

  // Next upcoming payment
  const upcoming = holdings
    .filter(h => h.pay_date && new Date(h.pay_date) >= now)
    .sort((a, b) => new Date(a.pay_date) - new Date(b.pay_date))[0];

  // Yield on cost
  const totalCost    = holdings.reduce((s, h) => s + (h.cost_basis || 0) * (h.shares || 0), 0);
  const yieldOnCost  = totalCost > 0 ? (annualIncome / totalCost * 100) : 0;

  // Market value & current yield
  const totalMarket  = holdings.reduce((s, h) => s + (prices[h.id]?.price || 0) * (h.shares || 0), 0);
  const currentYield = totalMarket > 0 ? (annualIncome / totalMarket * 100) : 0;

  // ── Calendar data ───────────────────────────────────────────────────────────
  const [calYear,  setCalYear]  = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());

  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  };

  const firstDay    = new Date(calYear, calMonth, 1).getDay();
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();

  const eventsByDay = {};
  holdings.forEach(h => {
    if (h.ex_date) {
      const d = new Date(h.ex_date);
      if (d.getMonth() === calMonth && d.getFullYear() === calYear) {
        const day = d.getDate();
        if (!eventsByDay[day]) eventsByDay[day] = [];
        eventsByDay[day].push({ ticker: h.ticker, type: "ex", color:"#f87171" });
      }
    }
    if (h.pay_date) {
      const d = new Date(h.pay_date);
      if (d.getMonth() === calMonth && d.getFullYear() === calYear) {
        const day = d.getDate();
        if (!eventsByDay[day]) eventsByDay[day] = [];
        eventsByDay[day].push({ ticker: h.ticker, type: "pay", color:C.green });
      }
    }
  });

  const statBox = (label, value, sub, color) => (
    <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, padding:"16px 20px", flex:1, minWidth:130 }}>
      <div style={{ fontFamily:FM, fontSize:8, fontWeight:700, color:"var(--text4)", letterSpacing:"0.15em", marginBottom:8 }}>{label}</div>
      <div style={{ fontFamily:FM, fontSize:22, fontWeight:900, color: color || "var(--text)", letterSpacing:"-0.01em" }}>{value}</div>
      {sub && <div style={{ fontFamily:FM, fontSize:10, color:"var(--text4)", marginTop:4 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ padding: isMobile ? "16px 12px" : "24px 28px", maxWidth:1100, margin:"0 auto" }}>

      {/* ── Header ── */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24, flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontFamily:FM, fontSize: isMobile ? 20 : 26, fontWeight:900, color:"var(--text)", letterSpacing:"-0.01em" }}>
            Dividend Tracker
          </div>
          <div style={{ fontFamily:FM, fontSize:11, color:"var(--text4)", marginTop:3 }}>
            {holdings.length} holding{holdings.length !== 1 ? "s" : ""} · {MONTHS[calMonth]} {calYear}
          </div>
        </div>
        <button onClick={() => { setEditItem(null); setShowModal(true); }} style={{
          background:C.orange, border:"none", borderRadius:10,
          padding:"11px 20px", fontFamily:FM, fontSize:10,
          fontWeight:800, letterSpacing:"0.15em", color:"#000", cursor:"pointer",
          display:"flex", alignItems:"center", gap:7,
        }}>
          <i className="ph ph-plus" style={{ fontSize:14 }}/>
          {!isMobile && "ADD HOLDING"}
        </button>
      </div>

      {/* ── Summary cards ── */}
      <div style={{ display:"flex", gap:12, marginBottom:24, flexWrap:"wrap" }}>
        {statBox("PROJECTED ANNUAL", fmt$(annualIncome), `${fmt$(monthlyIncome)}/mo avg`, C.green)}
        {statBox("RECEIVED YTD",     fmt$(ytdTotal),     `${ytdPayments.length} payment${ytdPayments.length !== 1 ? "s" : ""}`, C.orange)}
        {statBox("YIELD ON COST",    yieldOnCost.toFixed(2) + "%", totalCost > 0 ? `Cost basis ${fmt$(totalCost)}` : "—", "#a78bfa")}
        {statBox("CURRENT YIELD",    currentYield.toFixed(2) + "%", totalMarket > 0 ? `Market value ${fmt$(totalMarket)}` : "Fetching prices…", "#60a5fa")}
        {upcoming && statBox(
          "NEXT PAYMENT",
          upcoming.ticker,
          `${fmtDateShort(upcoming.pay_date)} · ${daysUntil(upcoming.pay_date)} days`,
          C.orange
        )}
      </div>

      {/* ── Monthly bar chart ── */}
      {payments.length > 0 && (
        <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, padding:"18px 20px", marginBottom:24 }}>
          <div style={{ fontFamily:FM, fontSize:9, fontWeight:700, color:"var(--text4)", letterSpacing:"0.15em", marginBottom:12 }}>
            MONTHLY INCOME {now.getFullYear()}
          </div>
          <MonthlyBarChart payments={ytdPayments}/>
        </div>
      )}

      {/* ── Tabs ── */}
      <div style={{ display:"flex", gap:6, marginBottom:20 }}>
        {[
          { key:"holdings", label:"Holdings",  icon:"ph-rows" },
          { key:"history",  label:"History",   icon:"ph-clock-counter-clockwise" },
          { key:"calendar", label:"Calendar",  icon:"ph-calendar" },
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            background: activeTab === t.key ? "rgba(242,135,5,0.1)" : "transparent",
            border:`1px solid ${activeTab === t.key ? C.orange : "var(--border)"}`,
            borderRadius:8, padding:"7px 14px", cursor:"pointer",
            fontFamily:FM, fontSize:9, fontWeight:700, letterSpacing:"0.1em",
            color: activeTab === t.key ? C.orange : "var(--text4)",
            display:"flex", alignItems:"center", gap:6, transition:"all 0.15s",
          }}>
            <i className={`ph ${t.icon}`} style={{ fontSize:13 }}/>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div style={{ textAlign:"center", padding:60, color:"var(--text4)", fontSize:11, letterSpacing:"0.3em" }}>LOADING…</div>
      )}

      {/* ── Empty state ── */}
      {!loading && holdings.length === 0 && (
        <div style={{ textAlign:"center", padding:"60px 20px", background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12 }}>
          <i className="ph ph-currency-circle-dollar" style={{ fontSize:40, color:"var(--text5)", display:"block", marginBottom:12 }}/>
          <div style={{ fontFamily:FM, fontSize:14, fontWeight:700, color:"var(--text4)", marginBottom:8 }}>No dividend holdings yet</div>
          <div style={{ fontFamily:FM, fontSize:11, color:"var(--text5)", marginBottom:20 }}>Add stocks that pay dividends to track your income</div>
          <button onClick={() => setShowModal(true)} style={{ background:C.orange, border:"none", borderRadius:8, padding:"10px 24px", fontFamily:FM, fontSize:10, fontWeight:800, letterSpacing:"0.15em", color:"#000", cursor:"pointer" }}>
            + ADD FIRST HOLDING
          </button>
        </div>
      )}

      {/* ── HOLDINGS TAB ── */}
      {!loading && holdings.length > 0 && activeTab === "holdings" && (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {holdings.map(h => {
            const price      = prices[h.id]?.price || 0;
            const mktValue   = price * (h.shares || 0);
            const annIncome  = (h.annual_dividend || 0) * (h.shares || 0);
            const perPayment = annIncome / (h.frequency === "monthly" ? 12 : h.frequency === "quarterly" ? 4 : h.frequency === "semi-annual" ? 2 : 1);
            const yoc        = h.cost_basis > 0 ? (h.annual_dividend / h.cost_basis * 100) : 0;
            const curYield   = price > 0 ? (h.annual_dividend / price * 100) : 0;
            const exDays     = daysUntil(h.ex_date);
            const payDays    = daysUntil(h.pay_date);
            const exSoon     = exDays !== null && exDays >= 0 && exDays <= 14;
            const paySoon    = payDays !== null && payDays >= 0 && payDays <= 14;

            return (
              <div key={h.id} style={{
                background:"var(--bg2)",
                border:`1px solid ${exSoon || paySoon ? "rgba(242,135,5,0.4)" : "var(--border)"}`,
                borderRadius:12, overflow:"hidden",
              }}>
                <div style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", flexWrap:"wrap" }}>
                  {/* Icon */}
                  <div style={{
                    width:40, height:40, borderRadius:10, flexShrink:0,
                    background:"rgba(242,135,5,0.1)", border:"1px solid rgba(242,135,5,0.2)",
                    display:"flex", alignItems:"center", justifyContent:"center",
                  }}>
                    <i className="ph ph-currency-circle-dollar" style={{ fontSize:18, color:C.orange }}/>
                  </div>

                  {/* Ticker */}
                  <div style={{ minWidth:100 }}>
                    <div style={{ fontFamily:FM, fontSize:14, fontWeight:900, color:"var(--text)" }}>{h.ticker}</div>
                    {h.name && h.name !== h.ticker && (
                      <div style={{ fontFamily:FM, fontSize:9, color:"var(--text4)", marginTop:1 }}>{h.name}</div>
                    )}
                    <div style={{ fontFamily:FM, fontSize:9, color:"var(--text4)", marginTop:1 }}>
                      {h.shares} shares
                      {h.frequency && <span style={{ marginLeft:6, background:"var(--bg3)", padding:"1px 6px", borderRadius:4, fontSize:8 }}>{h.frequency}</span>}
                    </div>
                  </div>

                  {!isMobile && <>
                    {/* Live price */}
                    <div style={{ minWidth:80 }}>
                      <div style={{ fontFamily:FM, fontSize:8, color:"var(--text4)", letterSpacing:"0.1em", marginBottom:2 }}>PRICE</div>
                      {pricesLoading && !price ? (
                        <div style={{ width:12, height:12, border:"2px solid var(--border)", borderTopColor:C.orange, borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
                      ) : (
                        <div style={{ fontFamily:FM, fontSize:12, fontWeight:700, color:"var(--text)" }}>{price ? fmtPrice(price) : "—"}</div>
                      )}
                      {mktValue > 0 && <div style={{ fontFamily:FM, fontSize:9, color:"var(--text4)" }}>{fmt$(mktValue)}</div>}
                    </div>

                    {/* Annual income */}
                    <div style={{ minWidth:90 }}>
                      <div style={{ fontFamily:FM, fontSize:8, color:"var(--text4)", letterSpacing:"0.1em", marginBottom:2 }}>ANNUAL INCOME</div>
                      <div style={{ fontFamily:FM, fontSize:13, fontWeight:800, color:C.green }}>{fmt$(annIncome)}</div>
                      <div style={{ fontFamily:FM, fontSize:9, color:"var(--text4)" }}>{fmt$(perPayment)}/payment</div>
                    </div>

                    {/* Yields */}
                    <div style={{ minWidth:80 }}>
                      <div style={{ fontFamily:FM, fontSize:8, color:"var(--text4)", letterSpacing:"0.1em", marginBottom:2 }}>YIELD</div>
                      <div style={{ fontFamily:FM, fontSize:12, fontWeight:700, color:"#a78bfa" }}>{yoc.toFixed(2)}% <span style={{ fontSize:8, color:"var(--text4)", fontWeight:400 }}>on cost</span></div>
                      {curYield > 0 && <div style={{ fontFamily:FM, fontSize:10, color:"var(--text4)" }}>{curYield.toFixed(2)}% current</div>}
                    </div>

                    {/* Ex-date */}
                    <div style={{ minWidth:90 }}>
                      <div style={{ fontFamily:FM, fontSize:8, color:"var(--text4)", letterSpacing:"0.1em", marginBottom:2 }}>EX-DATE</div>
                      {h.ex_date ? (
                        <>
                          <div style={{ fontFamily:FM, fontSize:11, fontWeight:700, color: exSoon ? "#f87171" : "var(--text2)" }}>{fmtDateShort(h.ex_date)}</div>
                          {exDays !== null && exDays >= 0 && <div style={{ fontFamily:FM, fontSize:9, color: exSoon ? "#f87171" : "var(--text4)" }}>{exDays === 0 ? "today" : `${exDays}d`}</div>}
                        </>
                      ) : <span style={{ fontFamily:FM, fontSize:11, color:"var(--text5)" }}>—</span>}
                    </div>

                    {/* Pay date */}
                    <div style={{ minWidth:90 }}>
                      <div style={{ fontFamily:FM, fontSize:8, color:"var(--text4)", letterSpacing:"0.1em", marginBottom:2 }}>PAY DATE</div>
                      {h.pay_date ? (
                        <>
                          <div style={{ fontFamily:FM, fontSize:11, fontWeight:700, color: paySoon ? C.green : "var(--text2)" }}>{fmtDateShort(h.pay_date)}</div>
                          {payDays !== null && payDays >= 0 && <div style={{ fontFamily:FM, fontSize:9, color: paySoon ? C.green : "var(--text4)" }}>{payDays === 0 ? "today" : `${payDays}d`}</div>}
                        </>
                      ) : <span style={{ fontFamily:FM, fontSize:11, color:"var(--text5)" }}>—</span>}
                    </div>
                  </>}

                  {/* Actions */}
                  <div style={{ marginLeft:"auto", display:"flex", gap:6, alignItems:"center", flexShrink:0 }}>
                    {/* Log payment */}
                    <button onClick={() => { setLogItem(h); setShowLogModal(true); }} style={{
                      background:"rgba(74,222,128,0.08)", border:`1px solid ${C.green}55`,
                      borderRadius:6, padding:"5px 10px", cursor:"pointer",
                      color:C.green, fontFamily:FM, fontSize:9, fontWeight:700,
                      display:"flex", alignItems:"center", gap:5, letterSpacing:"0.06em",
                    }}>
                      <i className="ph ph-plus-circle" style={{ fontSize:12 }}/>
                      {!isMobile && "LOG"}
                    </button>
                    {/* Edit */}
                    <button onClick={() => { setEditItem(h); setShowModal(true); }} style={{
                      background:"transparent", border:"1px solid var(--border)", borderRadius:6, padding:"5px 8px",
                      cursor:"pointer", color:"var(--text4)", transition:"all 0.15s", display:"flex", alignItems:"center",
                    }} onMouseEnter={e=>{e.currentTarget.style.borderColor=C.orange;e.currentTarget.style.color=C.orange;}} onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--border)";e.currentTarget.style.color="var(--text4)";}}>
                      <i className="ph ph-pencil" style={{ fontSize:13 }}/>
                    </button>
                    {/* Delete */}
                    {deleteConfirm === h.id ? (
                      <div style={{ display:"flex", gap:4 }}>
                        <button onClick={() => handleDelete(h.id)} style={{ background:"rgba(224,62,26,0.12)", border:"1px solid #E03E1A", color:"#E03E1A", borderRadius:6, padding:"5px 8px", cursor:"pointer", fontFamily:FM, fontSize:9, fontWeight:700 }}>YES</button>
                        <button onClick={() => setDeleteConfirm(null)} style={{ background:"transparent", border:"1px solid var(--border)", color:"var(--text4)", borderRadius:6, padding:"5px 8px", cursor:"pointer", fontFamily:FM, fontSize:9 }}>NO</button>
                      </div>
                    ) : (
                      <button onClick={() => setDeleteConfirm(h.id)} style={{
                        background:"transparent", border:"1px solid var(--border)", borderRadius:6, padding:"5px 8px",
                        cursor:"pointer", color:"var(--text4)", transition:"all 0.15s", display:"flex", alignItems:"center",
                      }} onMouseEnter={e=>{e.currentTarget.style.borderColor="#E03E1A";e.currentTarget.style.color="#E03E1A";}} onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--border)";e.currentTarget.style.color="var(--text4)";}}>
                        <i className="ph ph-trash" style={{ fontSize:13 }}/>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── HISTORY TAB ── */}
      {!loading && activeTab === "history" && (
        <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, overflow:"hidden" }}>
          {payments.length === 0 ? (
            <div style={{ textAlign:"center", padding:40 }}>
              <div style={{ fontFamily:FM, fontSize:12, color:"var(--text4)", marginBottom:8 }}>No payments logged yet</div>
              <div style={{ fontFamily:FM, fontSize:10, color:"var(--text5)" }}>Use the LOG button on each holding to record received dividends</div>
            </div>
          ) : (
            <>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 120px 120px 100px", padding:"10px 16px", background:"var(--bg3)", borderBottom:"1px solid var(--border)" }}>
                {["TICKER","DATE","AMOUNT",""].map((h, i) => (
                  <div key={i} style={{ fontFamily:FM, fontSize:8, fontWeight:700, color:"var(--text4)", letterSpacing:"0.15em" }}>{h}</div>
                ))}
              </div>
              {payments.map((p, idx) => (
                <div key={p.id} style={{
                  display:"grid", gridTemplateColumns:"1fr 120px 120px 100px",
                  padding:"12px 16px", alignItems:"center",
                  borderBottom: idx < payments.length - 1 ? "1px solid var(--border)" : "none",
                  background: idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)",
                }}>
                  <div style={{ fontFamily:FM, fontSize:13, fontWeight:900, color:"var(--text)" }}>{p.ticker}</div>
                  <div style={{ fontFamily:FM, fontSize:11, color:"var(--text3)" }}>{fmtDate(p.pay_date)}</div>
                  <div style={{ fontFamily:FM, fontSize:13, fontWeight:700, color:C.green }}>{fmt$(p.amount)}</div>
                  <button onClick={async () => {
                    if (demoItems) return;
                    await supabase.from("dividend_payments").delete().eq("id", p.id);
                    setPayments(prev => prev.filter(x => x.id !== p.id));
                  }} style={{ background:"transparent", border:"none", cursor:"pointer", color:"var(--text5)", fontSize:11, fontFamily:FM, fontWeight:700, textAlign:"left" }}
                    onMouseEnter={e => e.currentTarget.style.color="#E03E1A"}
                    onMouseLeave={e => e.currentTarget.style.color="var(--text5)"}>
                    remove
                  </button>
                </div>
              ))}
              <div style={{ padding:"12px 16px", borderTop:"1px solid var(--border)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ fontFamily:FM, fontSize:9, color:"var(--text4)", letterSpacing:"0.1em" }}>TOTAL RECEIVED</div>
                <div style={{ fontFamily:FM, fontSize:15, fontWeight:900, color:C.green }}>{fmt$(payments.reduce((s,p) => s + p.amount, 0))}</div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── CALENDAR TAB ── */}
      {!loading && activeTab === "calendar" && (
        <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, overflow:"hidden" }}>
          <div style={{ padding:"16px 20px", borderBottom:"1px solid var(--border)", display:"flex", alignItems:"center", gap:12 }}>
            {/* Prev/Next controls */}
            <button onClick={prevMonth} style={{ background:"transparent", border:"1px solid var(--border)", borderRadius:6, width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:"var(--text4)", flexShrink:0 }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=C.orange;e.currentTarget.style.color=C.orange;}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--border)";e.currentTarget.style.color="var(--text4)";}}>
              <i className="ph ph-caret-left" style={{ fontSize:12 }}/>
            </button>
            <div style={{ fontFamily:FM, fontSize:13, fontWeight:700, color:"var(--text)", minWidth:110, textAlign:"center" }}>
              {MONTHS[calMonth]} {calYear}
            </div>
            <button onClick={nextMonth} style={{ background:"transparent", border:"1px solid var(--border)", borderRadius:6, width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:"var(--text4)", flexShrink:0 }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=C.orange;e.currentTarget.style.color=C.orange;}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--border)";e.currentTarget.style.color="var(--text4)";}}>
              <i className="ph ph-caret-right" style={{ fontSize:12 }}/>
            </button>
            {/* Today button */}
            {(calMonth !== now.getMonth() || calYear !== now.getFullYear()) && (
              <button onClick={() => { setCalMonth(now.getMonth()); setCalYear(now.getFullYear()); }} style={{
                background:"transparent", border:"1px solid var(--border)", borderRadius:6,
                padding:"4px 10px", cursor:"pointer", fontFamily:FM, fontSize:8,
                fontWeight:700, color:"var(--text4)", letterSpacing:"0.1em",
              }}>TODAY</button>
            )}
            <div style={{ display:"flex", gap:16, marginLeft:"auto" }}>
              <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                <div style={{ width:8, height:8, borderRadius:"50%", background:"#f87171" }}/>
                <span style={{ fontFamily:FM, fontSize:9, color:"var(--text4)" }}>Ex-Date</span>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                <div style={{ width:8, height:8, borderRadius:"50%", background:C.green }}/>
                <span style={{ fontFamily:FM, fontSize:9, color:"var(--text4)" }}>Pay Date</span>
              </div>
            </div>
          </div>

          {/* Day headers */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7, 1fr)", padding:"8px 12px 4px" }}>
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
              <div key={d} style={{ fontFamily:FM, fontSize:8, fontWeight:700, color:"var(--text4)", textAlign:"center", letterSpacing:"0.08em" }}>{d}</div>
            ))}
          </div>

          {/* Calendar grid */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7, 1fr)", gap:2, padding:"4px 12px 16px" }}>
            {/* Empty cells before first day */}
            {Array(firstDay).fill(null).map((_, i) => <div key={`empty-${i}`}/>)}
            {/* Day cells */}
            {Array(daysInMonth).fill(null).map((_, i) => {
              const day     = i + 1;
              const isToday = day === now.getDate();
              const events  = eventsByDay[day] || [];
              return (
                <div key={day} style={{
                  minHeight:56, padding:"6px 4px", borderRadius:8,
                  background: isToday ? "rgba(242,135,5,0.08)" : "var(--bg3)",
                  border:`1px solid ${isToday ? "rgba(242,135,5,0.4)" : "var(--border)"}`,
                }}>
                  <div style={{ fontFamily:FM, fontSize:10, fontWeight: isToday ? 900 : 600, color: isToday ? C.orange : "var(--text3)", marginBottom:3 }}>{day}</div>
                  {events.map((ev, ei) => (
                    <div key={ei} style={{
                      fontFamily:FM, fontSize:7, fontWeight:700,
                      color: ev.type === "ex" ? "#f87171" : C.green,
                      background: ev.type === "ex" ? "rgba(248,113,113,0.1)" : "rgba(74,222,128,0.1)",
                      borderRadius:3, padding:"1px 3px", marginBottom:2, letterSpacing:"0.04em",
                      overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                    }}>
                      {ev.ticker} {ev.type === "ex" ? "EX" : "PAY"}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      {showModal && (
        <DividendModal item={editItem} onSave={handleSave} onClose={() => { setShowModal(false); setEditItem(null); }}/>
      )}
      {showLogModal && logItem && (
        <LogPaymentModal holding={logItem} onSave={handleLogPayment} onClose={() => { setShowLogModal(false); setLogItem(null); }}/>
      )}
    </div>
  );
}
